<template>
  <!-- "What's this?" (design 2026-10-26): three short beats, each with motion
       that teaches; with reduced motion every picture shows still. -->
  <Teleport to="body">
    <div class="ex-ov" @click.self="$emit('close')">
      <div ref="box" class="ex" :class="{ still: reduced }" role="dialog" aria-modal="true" aria-label="What is astrology?"
           data-testid="birth-explainer" tabindex="-1">
        <div class="ex-top">
          <div class="ex-dots" aria-hidden="true"><i v-for="i in 3" :key="i" :class="{ on: i - 1 === step }"></i></div>
          <button type="button" class="ex-close" aria-label="Close" @click="$emit('close')">×</button>
        </div>

        <template v-if="step === 0">
          <p class="ex-eyebrow">1 of 3 · What is astrology?</p>
          <h3>A snapshot of your sky</h3>
          <div :key="'p0'" :class="play">
            <svg class="ex-pic" viewBox="0 0 380 220" aria-hidden="true">
              <circle v-for="(s, i) in STARS" :key="i" :cx="s.x" :cy="s.y" :r="s.r" fill="#e9dcff" :opacity="s.o" />
              <ellipse cx="190" cy="110" rx="96" ry="60" fill="none" stroke="rgba(195,169,255,.18)" />
              <g class="orbit">
                <text v-for="o in ORBS" :key="o.g" :x="o.x" :y="o.y" :fill="o.c" :font-size="o.s" text-anchor="middle"
                      dominant-baseline="central" class="sym">{{ o.g }}</text>
              </g>
              <circle cx="190" cy="110" r="5" fill="#f0d9a3" />
              <text class="you-lbl" x="190" y="134">YOU, BORN</text>
              <path class="frame-corner" d="M78 30 h-16 v16 M302 30 h16 v16 M78 190 h-16 v-16 M302 190 h16 v-16" />
              <rect class="flash" x="0" y="0" width="380" height="220" />
              <text class="snap-cap" x="190" y="212">held still: your birth chart</text>
            </svg>
          </div>
          <p class="body">Astrology is an old way of reading the sky. At the moment you were born, the Sun, the Moon and the
            planets each sat in one part of it. That moment, held still, is your <b>birth chart</b>.</p>
        </template>

        <template v-else-if="step === 1">
          <p class="ex-eyebrow">2 of 3 · Why your date and time</p>
          <h3>Your three big signs</h3>
          <div :key="'p1'" :class="play">
            <svg class="ex-pic" viewBox="0 0 380 150" aria-hidden="true">
              <defs>
                <linearGradient :id="uid + 'dusk'" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stop-color="#1a0c33" /><stop offset="1" stop-color="#4a2550" />
                </linearGradient>
                <clipPath :id="uid + 'above'"><rect x="0" y="0" width="380" height="112" /></clipPath>
              </defs>
              <rect x="0" y="0" width="380" height="112" rx="12" :fill="`url(#${uid}dusk)`" />
              <g class="sun-pop"><circle cx="80" cy="48" r="16" fill="#f0d9a3" />
                <text x="80" y="82" fill="#f0d9a3" class="pic-lbl" text-anchor="middle">SUN</text></g>
              <g class="moon-pop"><path d="M300 32 a17 17 0 1 0 12 30 a13 13 0 1 1 -12 -30z" fill="#e9dcff" />
                <text x="302" y="82" fill="#e9dcff" class="pic-lbl" text-anchor="middle">MOON</text></g>
              <g :clip-path="`url(#${uid}above)`"><g class="rise">
                <text x="190" y="96" fill="#f0d9a3" font-size="34" text-anchor="middle" class="sym">{{ VIRGO }}</text></g></g>
              <line class="horizon" x1="10" y1="112" x2="370" y2="112" />
              <text x="190" y="134" fill="#c6b4e6" class="pic-lbl" text-anchor="middle">RISING: THE SIGN ON THE HORIZON</text>
            </svg>
          </div>
          <p class="body">The sky is split into twelve slices called <b>signs</b>, like Aries or Pisces. Three of them matter most:</p>
          <ul class="ex-list">
            <li :class="play" style="animation-delay:.1s"><span class="ic">☉︎</span><div><b>Sun sign</b>
              <span>Who you are at heart. <em>Your birth date is enough.</em></span></div></li>
            <li :class="play" style="animation-delay:.35s"><span class="ic">☽︎</span><div><b>Moon sign</b>
              <span>Your feelings and what comforts you. The Moon moves fast, so <em>your birth time pins it down.</em></span></div></li>
            <li :class="play" style="animation-delay:.6s"><span class="ic">↑︎</span><div><b>Rising sign</b>
              <span>How you come across when people first meet you. It is the sign coming up over the horizon as you were
                born, so <em>it needs your time and place.</em></span></div></li>
          </ul>
        </template>

        <template v-else>
          <p class="ex-eyebrow">3 of 3 · What it opens here</p>
          <h3>Your birthday opens</h3>
          <ul class="ex-list">
            <li v-for="(r, i) in opens" :key="r.title" :class="play" :style="{ animationDelay: (0.1 + i * 0.25) + 's' }">
              <span class="ic">{{ r.glyph }}</span><div><b>{{ r.title }}</b><span>{{ r.text }}</span></div></li>
          </ul>
          <p class="body body-after">Add your birth time and place later to see your Moon and Rising too.</p>
          <p class="body body-privacy" data-testid="ex-privacy">{{ COPY.privacy }}</p>
        </template>

        <div class="ex-nav">
          <button v-if="step > 0" type="button" class="ex-ghost" data-testid="ex-back" @click="step--">Back</button>
          <button v-if="step < 2" type="button" class="ex-btn" data-testid="ex-next" @click="step++">Next</button>
          <button v-else type="button" class="ex-btn gold" data-testid="ex-done" @click="$emit('start')">Add my birthday</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import '@/assets/fonts/letter/letter-fonts.css'
import { COPY } from '@/utils/birth-ask'

const props = defineProps({
  // The sender's or requester's name: adds the "You & <name>" row.
  withName: { type: String, default: '' },
  reduced: { type: Boolean, default: false },
})
const emit = defineEmits(['close', 'start'])

const step = ref(0)
const uid = `bmex${Math.random().toString(36).slice(2, 8)}`
const play = computed(() => (props.reduced ? '' : 'play'))
const VIRGO = '♍︎'

const rnd = (i) => ((Math.sin(i * 99.7) * 43758.5) % 1 + 1) % 1
const STARS = Array.from({ length: 40 }, (_, i) => ({
  x: (rnd(i) * 380).toFixed(0), y: (rnd(i + 50) * 200).toFixed(0), r: (rnd(i + 9) * 1.3 + 0.3).toFixed(1), o: (rnd(i + 3) * 0.6 + 0.2).toFixed(2),
}))
const ORBS = [['☉', 0, '#f0d9a3', 28, 78], ['☽', 200, '#e9dcff', 26, 74], ['♀', 250, '#d9a3c8', 20, 50], ['♂', 300, '#f0a3a3', 20, 88],
  ['♃', 140, '#c3a9ff', 20, 92]].map(([g, a, c, s, r]) => ({
  g: g + '︎', c, s, x: (190 + r * Math.cos(a * Math.PI / 180)).toFixed(0), y: (110 + r * 0.62 * Math.sin(a * Math.PI / 180)).toFixed(0),
}))

const opens = computed(() => {
  const rows = [
    { glyph: '☉︎', title: 'Your Mirror', text: 'Your signs, kept together in one place.' },
    { glyph: '✦︎', title: 'Your personalized horoscope', text: "Today's sky, read against your own birth chart, fresh each day on your Altar Home." },
  ]
  if (props.withName) rows.push({ glyph: '♡︎', title: `You & ${props.withName}`, text: `How your sky and ${props.withName}'s fit together.` })
  return rows
})

const box = ref(null)
function onKey (e) {
  if (e.key !== 'Escape') return
  e.preventDefault(); e.stopPropagation()
  emit('close')
}
onMounted(() => { document.addEventListener('keydown', onKey); nextTick(() => box.value && box.value.focus && box.value.focus()) })
onBeforeUnmount(() => document.removeEventListener('keydown', onKey))
</script>

<style scoped>
.ex-ov { position: fixed; inset: 0; z-index: 1450; background: rgba(6, 0, 14, 0.84); backdrop-filter: blur(4px); display: flex;
  align-items: center; justify-content: center; padding: 16px 12px; animation: exFade 0.25s ease both; }
.ex { --gold: #f0d9a3; width: 100%; max-width: 540px; max-height: 94vh; overflow-y: auto; box-sizing: border-box; outline: none;
  background: linear-gradient(180deg, #1a0b2c, #12061e); border: 1px solid rgba(240, 217, 163, 0.22); border-radius: 20px;
  padding: 20px 28px 26px; color: #e9dcff; font-family: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  animation: exPop 0.3s ease both; }
@keyframes exFade { from { opacity: 0 } to { opacity: 1 } }
@keyframes exPop { from { transform: scale(0.97); opacity: 0 } to { transform: none; opacity: 1 } }
@keyframes exUp { from { transform: translateY(40px); opacity: 0 } to { transform: none; opacity: 1 } }
.ex-top { display: flex; justify-content: space-between; align-items: center; }
.ex-dots { display: flex; gap: 6px; }
.ex-dots i { width: 22px; height: 4px; border-radius: 4px; background: rgba(195, 169, 255, 0.25); }
.ex-dots i.on { background: var(--gold); }
.ex-close { background: none; border: 0; color: #9b86c9; font-size: 26px; line-height: 1; cursor: pointer; min-width: 44px; min-height: 44px;
  margin: -8px -10px 0 0; }
.ex-eyebrow { margin: 6px 0 0; font: 600 11px 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: 0.2em; text-transform: uppercase; color: #9b86c9; }
.ex h3 { margin: 4px 0 0; font: 600 22px/1.25 'Letter Display', 'Cinzel', Georgia, serif; color: #f5e8c0; letter-spacing: 0.02em; }
.ex-pic { display: block; width: 100%; max-width: 380px; height: auto; margin: 10px auto 4px; overflow: visible; }
.sym { font-family: 'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', 'DejaVu Sans', sans-serif; }
.pic-lbl { font: 600 11px 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: 1.5px; }
.body { font-size: 18.5px; line-height: 1.45; font-weight: 500; color: #e2d4fa; margin: 8px 0 0; }
.body b { color: var(--gold); font-weight: 700; }
.body-after { margin-top: 14px; }
.body-privacy { margin-top: 8px; font-size: 0.92em; opacity: 0.85; }
.ex-list { list-style: none; margin: 12px 0 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.ex-list li { display: flex; gap: 12px; align-items: flex-start; padding: 10px 12px; border-radius: 14px; background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(195, 169, 255, 0.14); }
.ex-list .ic { flex: none; width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font: 22px 'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', sans-serif; color: var(--gold); background: rgba(240, 217, 163, 0.1);
  border: 1px solid rgba(240, 217, 163, 0.4); }
.ex-list b { display: block; font-size: 18px; font-weight: 700; color: #f6ecff; }
.ex-list span { display: block; font-size: 16px; line-height: 1.35; font-weight: 500; color: #c6b4e6; }
.ex-list span em { font-style: normal; color: var(--gold); }
.ex-list li.play { animation: rowIn 0.55s ease both; }
@keyframes rowIn { from { opacity: 0; transform: translateX(-8px) } to { opacity: 1; transform: none } }
.ex-nav { display: flex; gap: 10px; margin-top: 18px; }
.ex-btn { flex: 1; background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: 1px solid rgba(240, 217, 163, 0.35);
  border-radius: 999px; padding: 11px 24px; min-height: 48px; font: 700 18px 'Letter Serif', Georgia, serif; cursor: pointer; }
.ex-btn.gold { background: linear-gradient(180deg, #f3d58f, #d9b25e); color: #2a1845; border: 0; }
.ex-ghost { background: transparent; color: #9b86c9; border: 1px solid rgba(155, 134, 201, 0.4); border-radius: 999px; padding: 10px 18px;
  min-height: 48px; font: 600 17px 'Letter Serif', Georgia, serif; cursor: pointer; }
.ex-btn:focus-visible, .ex-ghost:focus-visible, .ex-close:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }

/* beat 1: the sky turns, then the moment is held still */
.flash { fill: #fff; opacity: 0; }
.play .flash { animation: flash 2.6s ease 1 both; }
@keyframes flash { 0%, 68% { opacity: 0 } 72% { opacity: 0.55 } 100% { opacity: 0 } }
.orbit { transform-origin: 190px 110px; }
.play .orbit { animation: spin 2s cubic-bezier(0.3, 0.6, 0.2, 1) 1 both; }
@keyframes spin { from { transform: rotate(-200deg) } to { transform: rotate(0) } }
.frame-corner { stroke: var(--gold); stroke-width: 2; fill: none; opacity: 1; }
.play .frame-corner, .play .you-lbl, .play .snap-cap { animation: cornerIn 2.6s ease 1 both; }
@keyframes cornerIn { 0%, 70% { opacity: 0 } 78%, 100% { opacity: 1 } }
.you-lbl { font: 600 11px 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: 0.14em; fill: var(--gold); text-anchor: middle; }
.snap-cap { font: italic 600 15px 'Letter Serif', Georgia, serif; fill: #e2d4fa; text-anchor: middle; }
/* beat 2: the Sun and Moon appear, the Rising sign comes up over the horizon */
.horizon { stroke: rgba(240, 217, 163, 0.5); stroke-width: 1.5; }
.rise { transform-box: fill-box; }
.play .rise { animation: riseUp 2.4s cubic-bezier(0.2, 0.7, 0.2, 1) 0.3s 1 both; }
@keyframes riseUp { from { transform: translateY(46px); opacity: 0.15 } to { transform: none; opacity: 1 } }
.sun-pop, .moon-pop { transform-box: fill-box; transform-origin: center; }
.play .sun-pop { animation: pop 0.7s ease 0.1s 1 both; }
.play .moon-pop { animation: pop 0.7s ease 0.5s 1 both; }
@keyframes pop { from { opacity: 0; transform: scale(0.6) } to { opacity: 1; transform: none } }

@media (max-width: 600px) {
  .ex-ov { align-items: flex-end; padding: 0; }
  .ex { max-width: none; border-bottom: 0; border-radius: 22px 22px 0 0; padding: 14px 20px 22px;
    padding-bottom: calc(22px + env(safe-area-inset-bottom, 0px)); animation-name: exUp; animation-duration: 0.35s; }
}
@media (prefers-reduced-motion: reduce) {
  .ex-ov, .ex, .ex * { animation: none !important; }
}
.still, .still * { animation: none !important; }
</style>
